import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewChild, Input, Output } from '@angular/core';
import { CodeJar } from 'codejar';
import { withLineNumbers } from 'codejar-linenumbers';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class NgxCodeJarComponent {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this._code = '';
        this.height = undefined;
        this.showLineNumbers = false;
        this.codeChange = new EventEmitter();
        this.highlighter = 'hljs';
        this.options = {};
        this.readonly = false;
        this.highlightMethod = () => {
        };
        this.onKeyDown = (event) => {
            if (this.readonly) {
                event.stopPropagation();
                event.preventDefault();
            }
        };
        this.update = new EventEmitter();
    }
    set code(value) {
        if (this._code !== value) {
            this._code = value;
            this.updateCode(value);
        }
    }
    ngOnChanges(changes) {
        if (changes['options']?.currentValue && changes['options'].currentValue !== changes['options']) {
            if (this.codeJar) {
                this.codeJar.updateOptions(changes['options']?.currentValue);
                this.applyAdditionalOptions();
                this.checkReadonly();
            }
        }
        if (changes['readonly']) {
            this.checkReadonly();
        }
    }
    checkReadonly() {
        if (this.editor?.nativeElement) {
            if (this.readonly) {
                this.renderer.setAttribute(this.editor?.nativeElement, 'contenteditable', 'false');
            }
            else {
                if (/Firefox/.test(navigator.userAgent)) {
                    this.renderer.setAttribute(this.editor?.nativeElement, 'contenteditable', 'true');
                }
                else {
                    this.renderer.setAttribute(this.editor?.nativeElement, 'contenteditable', 'plaintext-only');
                }
            }
        }
    }
    ngAfterViewInit() {
        this.init();
    }
    init() {
        if (this.editor !== undefined) {
            const highlightMethod = (this.showLineNumbers) ? withLineNumbers(this.highlightMethod) : this.highlightMethod;
            this.codeJar = CodeJar(this.editor.nativeElement, highlightMethod, this.options);
            this.applyCustomizations();
            this.applyAdditionalOptions();
            this.codeJar.onUpdate((newCode) => {
                this._code = newCode;
                this.codeChange.emit(newCode);
                this.update.emit(newCode);
            });
            this.updateCode(this._code);
            this.update.emit(this._code);
            this.editor.nativeElement.addEventListener('keydown', this.onKeyDown);
            this.checkReadonly();
        }
    }
    applyAdditionalOptions() {
        if (this.editor) {
            if (this.options.tabSize) {
                this.renderer.setStyle(this.editor.nativeElement, 'tab-size', this.options.tabSize);
            }
        }
    }
    /***
     * updates the code and preserves the position
     * @param newCode new code
     */
    updateCode(newCode) {
        if (this.codeJar) {
            this.codeJar.updateCode(newCode);
        }
    }
    /***
     * updates the options.
     * @param options new options
     */
    updateOptions(options) {
        if (this.codeJar) {
            this.options = options;
            this.codeJar.updateOptions(options);
        }
    }
    /***
     * saves current cursor position.
     */
    save() {
        if (this.codeJar) {
            return this.codeJar.save();
        }
        return undefined;
    }
    /***
     * restore cursor position.
     * @param pos cursor position
     */
    restore(pos) {
        if (this.codeJar) {
            this.codeJar.restore(pos);
        }
    }
    /***
     * saves current editor state to history.
     */
    recordHistory(pos) {
        if (this.codeJar) {
            this.codeJar.recordHistory();
        }
    }
    /***
     * removes all event listeners. It's automatically called when component is destroyed.
     */
    destroy() {
        if (this.codeJar) {
            this.codeJar.destroy();
        }
    }
    ngOnDestroy() {
        this.destroy();
    }
    applyCustomizations() {
        if (this.wrapper && this.editor && this.el?.nativeElement) {
            const codejarWrap = this.wrapper.nativeElement.firstElementChild;
            const lineNumbers = this.el.nativeElement.getElementsByClassName('codejar-linenumbers')[0];
            if (codejarWrap) {
                this.renderer.setStyle(codejarWrap, 'height', '100%');
            }
            if (lineNumbers) {
                if (this.el.nativeElement.style?.height) {
                    this.renderer.setStyle(lineNumbers, 'height', '100%');
                }
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxCodeJarComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: NgxCodeJarComponent, isStandalone: true, selector: "ngx-codejar", inputs: { code: "code", showLineNumbers: "showLineNumbers", highlighter: "highlighter", options: "options", readonly: "readonly", highlightMethod: "highlightMethod" }, outputs: { codeChange: "codeChange", update: "update" }, viewQueries: [{ propertyName: "editor", first: true, predicate: ["editor"], descendants: true }, { propertyName: "wrapper", first: true, predicate: ["wrapper"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div class="ngx-codejar-wrap ngx-codejar-flex" #wrapper>
        <pre #editor class="editor" [ngClass]="{
                'hljs': highlighter === 'hljs',
                'language-typescript': highlighter === 'prism',
                'ngx-codejar-editor': highlighter !== undefined
             }" style="padding-bottom:0px;">
        </pre>
    </div>`, isInline: true, styles: [":host{display:block}.ngx-codejar-editor{border-radius:6px;box-shadow:0 2px 2px #00000024,0 1px 5px #0000001f,0 3px 1px -2px #0003;font-family:Source Code Pro,monospace;font-size:14px;font-weight:400;letter-spacing:normal;line-height:20px;padding:10px;tab-size:4;margin:0;height:100%}.ngx-codejar-wrap{height:100%}.ngx-codejar-flex{display:flex!important;flex-direction:column!important;flex:auto!important;height:100%!important}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgxCodeJarComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, imports: [CommonModule], selector: 'ngx-codejar', template: `
    <div class="ngx-codejar-wrap ngx-codejar-flex" #wrapper>
        <pre #editor class="editor" [ngClass]="{
                'hljs': highlighter === 'hljs',
                'language-typescript': highlighter === 'prism',
                'ngx-codejar-editor': highlighter !== undefined
             }" style="padding-bottom:0px;">
        </pre>
    </div>`, styles: [":host{display:block}.ngx-codejar-editor{border-radius:6px;box-shadow:0 2px 2px #00000024,0 1px 5px #0000001f,0 3px 1px -2px #0003;font-family:Source Code Pro,monospace;font-size:14px;font-weight:400;letter-spacing:normal;line-height:20px;padding:10px;tab-size:4;margin:0;height:100%}.ngx-codejar-wrap{height:100%}.ngx-codejar-flex{display:flex!important;flex-direction:column!important;flex:auto!important;height:100%!important}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { editor: [{
                type: ViewChild,
                args: ['editor']
            }], wrapper: [{
                type: ViewChild,
                args: ['wrapper']
            }], code: [{
                type: Input
            }], showLineNumbers: [{
                type: Input
            }], codeChange: [{
                type: Output
            }], highlighter: [{
                type: Input
            }], options: [{
                type: Input
            }], readonly: [{
                type: Input
            }], update: [{
                type: Output
            }], highlightMethod: [{
                type: Input
            }] } });

/*
 * Public API Surface of ngx-codejar
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxCodeJarComponent };
